import React, { useState, useEffect } from "react";
import { 
  Box, 
  Container, 
  CssBaseline, 
  CircularProgress, 
  Typography, 
  ThemeProvider,
  Paper,
  Fade
} from '@mui/material';
import Header from './components/Header';
import Footer from './components/Footer';
import VideoGrid from './components/VideoGrid';
import AppLinks from './components/AppLinks';
import theme from './theme';

// Import main CSS
import "./App.css";

function App() {
  // State
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Theme toggle function
  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
    localStorage.setItem("freeweb3-theme", !isDarkMode ? "dark" : "light");
  };

  // Load saved theme and simulate loading on first render
  useEffect(() => {
    // Load saved theme preference
    const savedTheme = localStorage.getItem("freeweb3-theme");
    if (savedTheme === "dark") {
      setIsDarkMode(true);
    }

    // Simulate loading for better UX
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  // Loading screen component
  const LoadingScreen = () => (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: theme.palette.background.default,
        color: theme.palette.primary.main
      }}
    >
      <CircularProgress size={60} thickness={4} />
      <Typography variant="h6" sx={{ mt: 3 }}>
        Loading Evidencer...
      </Typography>
    </Box>
  );

  // Main content component
  const MainContent = () => (
    <Box component="main" sx={{ mt: 12, mb: 4 }}>
      <Container maxWidth="lg">
        <Fade in={!isLoading} timeout={1000}>
          <Box>
            <Box 
              sx={{ 
                textAlign: 'center', 
                mb: 6 
              }}
            >
              <Typography 
                variant="h2" 
                component="h1" 
                gutterBottom
                sx={{ 
                  fontWeight: 700,
                  color: theme.palette.secondary.main 
                }}
              >
                id➡store➡stamp
              </Typography>
              
              <Typography 
                variant="h5"
                color="primary" 
                sx={{ 
                  mb: 4, 
                  maxWidth: '700px', 
                  mx: 'auto' 
                }}
              >
                blockchain-based digital file creator
              </Typography>
            </Box>
            
            <VideoGrid />
            
            <Box sx={{ py: 3 }}>
              <Paper 
                elevation={0} 
                sx={{ 
                  bgcolor: 'background.light', 
                  p: 3, 
                  borderRadius: 2,
                  background: 'linear-gradient(145deg, #f5f7fe 0%, #ffffff 100%)',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Typography 
                  variant="body1" 
                  align="center" 
                  sx={{ 
                    mb: 2,
                    color: theme.palette.text.secondary,
                    fontSize: '1.1rem',
                    lineHeight: 1.7
                  }}
                >
                  Evidencer provides a complete ecosystem of tools for secure identity creation, content storage, 
                  and blockchain timestamping. Our platform allows legal professionals, organizations, and individuals 
                  to create tamper-proof digital evidence that can be verified and authenticated.
                </Typography>
              </Paper>
            </Box>
            
            <AppLinks />
          </Box>
        </Fade>
      </Container>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box 
        className={isDarkMode ? "dark-mode" : ""} 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          minHeight: '100vh' 
        }}
      >
        <Header isDarkMode={isDarkMode} toggleTheme={toggleTheme} />
        {isLoading ? <LoadingScreen /> : <MainContent />}
        <Footer />
      </Box>
    </ThemeProvider>
  );
}

export default App;